import { Skeleton } from '@/components/ui/skeleton';
import { semanticTokens } from '@packages/config/theme';
import { EmptyState, Page, PageToolbar } from '@supabank/ui';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { QrCode } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { QR } from '../components/ui/QR';
import { QRCodeDialog } from '../components/QRCodeDialog';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useVirtualAccount } from '../hooks/useTxTimeline';
import { cn } from '../lib/utils';

interface VirtualAccountDetailPageProps {
  id: string;
  onBack?: () => void;
}

export const VirtualAccountDetailPage: React.FC<VirtualAccountDetailPageProps> = ({ id, onBack }) => {
  const navigate = useNavigate();
  const { account, loading, error, reload } = useVirtualAccount(id);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const copyAllDetails = () => {
    if (!account?.deposit_instructions) return;

    const instructions = account.deposit_instructions;
    const details = [
      `Virtual Account Details - ${account.source_currency?.toUpperCase()}`,
      '',
      'Bank Details:',
      instructions.bank_name && `Bank Name: ${instructions.bank_name}`,
      instructions.iban && `IBAN: ${instructions.iban}`,
      instructions.swift && `SWIFT/BIC: ${instructions.swift}`,
      instructions.account_number && `Account Number: ${instructions.account_number}`,
      instructions.routing && `Routing Number: ${instructions.routing}`,
      instructions.clabe && `CLABE: ${instructions.clabe}`,
      (instructions.beneficiary_name || instructions.account_holder_name || instructions.bank_beneficiary_name) && 
        `Account Holder: ${instructions.beneficiary_name || instructions.account_holder_name || instructions.bank_beneficiary_name}`,
      '',
      'How to send funds:',
      `1. Use the bank details above to send ${account.source_currency?.toUpperCase()}`,
      '2. Funds will be automatically converted and delivered to your crypto destination',
      '3. You\'ll receive a notification when the transfer is complete',
    ].filter(Boolean).join('\n');

    copyToClipboard(details);
    toast.success('All details copied to clipboard');
  };

  const downloadDetails = () => {
    if (!account?.deposit_instructions) return;

    const instructions = account.deposit_instructions;
    const details = [
      `Virtual Account Details - ${account.source_currency?.toUpperCase()}`,
      `Bridge ID: ${account.bridge_va_id || 'N/A'}`,
      `Status: ${typeof account.status === 'string' ? account.status : account.status?.status || 'Unknown'}`,
      `Created: ${new Date(account.created_at).toLocaleString()}`,
      '',
      'Bank Details:',
      instructions.bank_name && `Bank Name: ${instructions.bank_name}`,
      instructions.iban && `IBAN: ${instructions.iban}`,
      instructions.swift && `SWIFT/BIC: ${instructions.swift}`,
      instructions.account_number && `Account Number: ${instructions.account_number}`,
      instructions.routing && `Routing Number: ${instructions.routing}`,
      instructions.clabe && `CLABE: ${instructions.clabe}`,
      (instructions.beneficiary_name || instructions.account_holder_name || instructions.bank_beneficiary_name) && 
        `Account Holder: ${instructions.beneficiary_name || instructions.account_holder_name || instructions.bank_beneficiary_name}`,
      '',
      'How to send funds:',
      `1. Use the bank details above to send ${account.source_currency?.toUpperCase()}`,
      '2. Funds will be automatically converted and delivered to your crypto destination',
      '3. You\'ll receive a notification when the transfer is complete',
    ].filter(Boolean).join('\n');

    const blob = new Blob([details], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `virtual-account-${account.source_currency}-${account.bridge_va_id?.slice(0, 8) || 'details'}.txt`;
    link.click();
    toast.success('Details downloaded');
  };

  // Generate EPC QR Code for SEPA (European Payments Council standard)
  // Mobile banking apps in EU automatically recognize and prefill payment
  const generateEpcQrData = (instructions: any, currency: string): string | null => {
    if (!instructions.iban) return null;
    
    const accountHolderName = instructions.beneficiary_name || 
                              instructions.account_holder_name || 
                              instructions.bank_beneficiary_name || '';
    const bic = instructions.swift || instructions.bic || '';
    const reference = instructions.reference || '';

    // EPC QR Code Format (ISO 20022)
    // Line 1: Service Tag (fixed)
    // Line 2: Version (002)
    // Line 3: Character Set (1 = UTF-8)
    // Line 4: Identification (SCT = SEPA Credit Transfer)
    // Line 5: BIC (optional)
    // Line 6: Beneficiary Name
    // Line 7: IBAN
    // Line 8: Amount (EUR, empty = user enters)
    // Line 9: Purpose (empty)
    // Line 10: Structured Reference (empty)
    // Line 11: Unstructured Remittance (Reference/Memo)
    // Line 12: Beneficiary to Originator Information (empty)
    
    const epcLines = [
      'BCD',                    // Service Tag
      '002',                    // Version
      '1',                      // Character Set (UTF-8)
      'SCT',                    // Identification (SEPA Credit Transfer)
      bic,                      // BIC (optional)
      accountHolderName,        // Beneficiary Name
      instructions.iban,        // IBAN
      `${currency.toUpperCase()}`,  // Amount Currency (empty amount = user enters)
      '',                       // Purpose (empty)
      '',                       // Structured Reference (empty)
      reference,                // Unstructured Remittance
      '',                       // Beneficiary to Originator Info
    ];
    
    return epcLines.join('\n');
  };

  const qrData = useMemo(() => {
    if (!account?.deposit_instructions) return '';

    const instructions = account.deposit_instructions;
    const currency = account.source_currency || 'EUR';

    // Get account holder name (try multiple fields)
    const accountHolderName = instructions.beneficiary_name || 
                              instructions.account_holder_name || 
                              instructions.bank_beneficiary_name;

    // For SEPA (IBAN) - use EPC QR Code standard for EUR
    if (instructions.iban && currency.toUpperCase() === 'EUR') {
      const epcData = generateEpcQrData(instructions, currency);
      if (epcData) return epcData;
    }

    // For SEPA non-EUR or fallback - use structured text
    if (instructions.iban) {
      const lines = [
        accountHolderName ? `Account Holder: ${accountHolderName}` : '',
        `IBAN: ${instructions.iban}`,
        instructions.swift ? `BIC: ${instructions.swift}` : '',
        instructions.bank_name ? `Bank: ${instructions.bank_name}` : '',
      ].filter(Boolean);
      return lines.join('\n');
    }

    // For US ACH/Wire
    if (instructions.routing && instructions.account_number) {
      const lines = [
        accountHolderName ? `Account Holder: ${accountHolderName}` : '',
        `Routing: ${instructions.routing}`,
        `Account: ${instructions.account_number}`,
        instructions.bank_name ? `Bank: ${instructions.bank_name}` : '',
      ].filter(Boolean);
      return lines.join('\n');
    }

    // For MX SPEI (CLABE)
    if (instructions.clabe) {
      const lines = [
        accountHolderName ? `Account Holder: ${accountHolderName}` : '',
        `CLABE: ${instructions.clabe}`,
        instructions.bank_name ? `Bank: ${instructions.bank_name}` : '',
      ].filter(Boolean);
      return lines.join('\n');
    }

    // Fallback: JSON format
    return JSON.stringify(instructions, null, 2);
  }, [account?.deposit_instructions, account?.source_currency]);

  const handleRefresh = () => {
    reload();
    toast.success('Virtual account refreshed');
  };

  const toolbarItems = [
    {
      id: 'refresh',
      hotkey: 'R',
      ariaLabel: 'Refresh virtual account',
      onRefresh: handleRefresh,
      isLoading: loading
    }
  ];

  if (loading) {
    return (
      <Page data-ux-contract="canon">
        <PageHeader
          icon={() => <SFIcon icon={sfIcons.building2} />}
          title="Bank Account Details"
          subtitle="Loading..."
        />
        <PageContent>
          <Skeleton className={cn('h-[300px] w-full', semanticTokens.radius.lg)} />
        </PageContent>
      </Page>
    );
  }

  if (error) {
    return (
      <Page data-ux-contract="canon">
        <PageHeader
          icon={() => <SFIcon icon={sfIcons.building2} />}
          title="Bank Account Details"
          subtitle="Error loading account"
        />
        <PageContent>
          <EmptyState
            icon={() => <SFIcon icon={sfIcons.building2} />}
            title="Failed to load bank account"
            description={error || 'An error occurred while loading the bank account details'}
            action={{
              label: 'Retry',
              onClick: () => reload()
            }}
          />
        </PageContent>
      </Page>
    );
  }

  if (!account) {
    return (
      <Page data-ux-contract="canon">
        <PageHeader
          icon={() => <SFIcon icon={sfIcons.building2} />}
          title="Bank Account Details"
          subtitle="Account not found"
        />
        <PageContent>
          <EmptyState
            icon={() => <SFIcon icon={sfIcons.building2} />}
            title="Bank account not found"
            description="The requested bank account could not be found"
            action={onBack ? {
              label: 'Back to Accounts',
              onClick: onBack
            } : {
              label: 'Back to Accounts',
              onClick: () => navigate('/dashboard/receive')
            }}
          />
        </PageContent>
      </Page>
    );
  }

  return (
    <Page data-ux-contract="canon">
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard/virtual-accounts')}
          className={cn(
            "min-h-[44px]",
            semanticTokens.focus.ring,
            semanticTokens.motion.standard
          )}
          aria-label="Back to virtual accounts"
        >
          <SFIcon icon={sfIcons.back} className="w-4 h-4 mr-2" aria-hidden="true" />
          Back to Virtual Accounts
        </Button>
      </div>

      <PageHeader
        icon={() => <SFIcon icon={sfIcons.building2} />}
        title={`${account.source_currency?.toUpperCase()} Bank Account`}
        subtitle={`Send ${account.source_currency?.toUpperCase()} to receive crypto automatically`}
      />

      <PageToolbar items={toolbarItems} />

      <PageContent>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Bank Account Details</CardTitle>
                  <p className={cn("text-sm mt-1", semanticTokens.fg.muted)}>Use these details to send {account.source_currency?.toUpperCase()} transfers</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyAllDetails}
                    className="gap-2"
                  >
                    <SFIcon icon={sfIcons.copy} className="w-4 h-4" />
                    Copy All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadDetails}
                    className="gap-2"
                  >
                    <SFIcon icon={sfIcons.download} className="w-4 h-4" />
                    Download
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {account.deposit_instructions ? (
                  <div className="space-y-4">
                    <div className="grid gap-3">
                      {/* Account Holder / Beneficiary Name - PROMINENT: First and most important */}
                      {(account.deposit_instructions.beneficiary_name || 
                        account.deposit_instructions.account_holder_name || 
                        account.deposit_instructions.bank_beneficiary_name) && (
                        <div className={cn(
                          "flex items-start gap-4 p-4 rounded-lg border-2",
                          semanticTokens.info.bg,
                          semanticTokens.info.border,
                          semanticTokens.radius.lg
                        )}>
                          <div className={cn(
                            "flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center",
                            semanticTokens.info.bg,
                            "border-2",
                            semanticTokens.info.border
                          )}>
                            <SFIcon icon={sfIcons.user} className={cn("w-6 h-6", semanticTokens.info.accent)} aria-hidden="true" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={cn(
                              "flex items-center gap-2 mb-1",
                              semanticTokens.typography.label,
                              semanticTokens.info.fg
                            )}>
                              Account Holder Name
                              <SFIcon icon={sfIcons.verify} className={cn("w-4 h-4", semanticTokens.info.accent)} aria-hidden="true" />
                            </div>
                            <div className={cn(
                              semanticTokens.typography.h3,
                              semanticTokens.fg.default,
                              "break-words"
                            )}>
                              {account.deposit_instructions.beneficiary_name || 
                               account.deposit_instructions.account_holder_name || 
                               account.deposit_instructions.bank_beneficiary_name}
                            </div>
                            <div className={cn(
                              semanticTokens.typography.caption,
                              semanticTokens.fg.muted,
                              "mt-1"
                            )}>
                              Use this exact name when sending funds
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(
                              account.deposit_instructions.beneficiary_name || 
                              account.deposit_instructions.account_holder_name || 
                              account.deposit_instructions.bank_beneficiary_name
                            )}
                            className={cn("min-h-[44px] min-w-[44px]", semanticTokens.focus.ring)}
                            aria-label="Copy account holder name"
                          >
                            <SFIcon icon={sfIcons.copy} className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {/* EU SEPA Instructions */}
                      {account.deposit_instructions.iban && (
                        <div className={cn("flex items-center justify-between p-3 rounded-lg", semanticTokens.bg.muted, semanticTokens.radius.lg)}>
                          <div className="flex-1 min-w-0">
                            <div className={cn("mb-1", semanticTokens.typography.label, semanticTokens.fg.muted)}>IBAN</div>
                            <div className={cn("font-mono break-all", semanticTokens.typography.bodyLarge, semanticTokens.fg.default)}>{account.deposit_instructions.iban}</div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(account.deposit_instructions.iban)}
                            className={cn("ml-2 min-h-[44px] min-w-[44px]", semanticTokens.focus.ring)}
                            aria-label="Copy IBAN"
                          >
                            <SFIcon icon={sfIcons.copy} className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {/* US ACH/Wire Instructions */}
                      {account.deposit_instructions.routing && (
                        <div className={cn("flex items-center justify-between p-3 rounded-lg", semanticTokens.bg.muted, semanticTokens.radius.lg)}>
                          <div className="flex-1 min-w-0">
                            <div className={cn("mb-1", semanticTokens.typography.label, semanticTokens.fg.muted)}>Routing Number</div>
                            <div className={cn("font-mono", semanticTokens.typography.bodyLarge, semanticTokens.fg.default)}>{account.deposit_instructions.routing}</div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(account.deposit_instructions.routing)}
                            className={cn("ml-2 min-h-[44px] min-w-[44px]", semanticTokens.focus.ring)}
                            aria-label="Copy routing number"
                          >
                            <SFIcon icon={sfIcons.copy} className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {account.deposit_instructions.account_number && (
                        <div className={cn("flex items-center justify-between p-3 rounded-lg", semanticTokens.bg.muted, semanticTokens.radius.lg)}>
                          <div className="flex-1 min-w-0">
                            <div className={cn("mb-1", semanticTokens.typography.label, semanticTokens.fg.muted)}>Account Number</div>
                            <div className={cn("font-mono", semanticTokens.typography.bodyLarge, semanticTokens.fg.default)}>{account.deposit_instructions.account_number}</div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(account.deposit_instructions.account_number)}
                            className={cn("ml-2 min-h-[44px] min-w-[44px]", semanticTokens.focus.ring)}
                            aria-label="Copy account number"
                          >
                            <SFIcon icon={sfIcons.copy} className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {/* MX SPEI Instructions */}
                      {account.deposit_instructions.clabe && (
                        <div className={cn("flex items-center justify-between p-3 rounded-lg", semanticTokens.bg.muted, semanticTokens.radius.lg)}>
                          <div className="flex-1 min-w-0">
                            <div className={cn("mb-1", semanticTokens.typography.label, semanticTokens.fg.muted)}>CLABE</div>
                            <div className={cn("font-mono", semanticTokens.typography.bodyLarge, semanticTokens.fg.default)}>{account.deposit_instructions.clabe}</div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(account.deposit_instructions.clabe)}
                            className={cn("ml-2 min-h-[44px] min-w-[44px]", semanticTokens.focus.ring)}
                            aria-label="Copy CLABE"
                          >
                            <SFIcon icon={sfIcons.copy} className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {/* Bank Details */}
                      {account.deposit_instructions.bank_name && (
                        <div className={cn("p-3 rounded-lg", semanticTokens.bg.muted, semanticTokens.radius.lg)}>
                          <div className={cn("mb-1", semanticTokens.typography.label, semanticTokens.fg.muted)}>Bank Name</div>
                          <div className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>{account.deposit_instructions.bank_name}</div>
                        </div>
                      )}

                      {account.deposit_instructions.swift && (
                        <div className={cn("p-3 rounded-lg", semanticTokens.bg.muted, semanticTokens.radius.lg)}>
                          <div className={cn("mb-1", semanticTokens.typography.label, semanticTokens.fg.muted)}>SWIFT/BIC Code</div>
                          <div className={cn("font-mono", semanticTokens.typography.body, semanticTokens.fg.default)}>{account.deposit_instructions.swift}</div>
                        </div>
                      )}
                    </div>

                    {/* Instructions Summary */}
                    <div className="pt-4 border-t">
                      <div className={cn("text-sm mb-2", semanticTokens.fg.muted)}>
                        How to send funds:
                      </div>
                      <ol className={cn("text-sm space-y-1 list-decimal list-inside", semanticTokens.fg.default)}>
                        <li>Use the bank details above to send {account.source_currency?.toUpperCase()}</li>
                        <li>Funds will be automatically converted and delivered to your crypto destination</li>
                        <li>You'll receive a notification when the transfer is complete</li>
                      </ol>
                    </div>
                  </div>
                ) : (
                  <div className={cn("text-center py-4", semanticTokens.fg.muted)}>
                    No deposit instructions available
                  </div>
                )}
              </CardContent>
            </Card>

            <TransactionTimeline accountId={account.id} />
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className={cn("flex items-center gap-2", semanticTokens.typography.h4)}>
                    <QrCode className="w-5 h-5" />
                    QR Code
                  </CardTitle>
                  {/* EPC Standard Badge for EUR SEPA */}
                  {account.deposit_instructions?.iban && account.source_currency?.toUpperCase() === 'EUR' && (
                    <Badge variant="secondary" className={cn("text-xs", semanticTokens.positive.fg, semanticTokens.positive.bg)}>
                      EPC Standard
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-4">
                {qrData ? (
                  <>
                    <QR value={qrData} size={200} alt="Deposit Instructions QR" />
                    
                    {/* Expandable QR Dialog */}
                    <QRCodeDialog
                      address={qrData}
                      asset={account.source_currency?.toUpperCase() || 'EUR'}
                      chain={account.deposit_instructions?.iban ? 'SEPA' : 'Bank Transfer'}
                      label={`${account.source_currency?.toUpperCase()} Bank Account Details`}
                      trigger={
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn("gap-2 min-h-[44px]", semanticTokens.focus.ring, semanticTokens.motion.standard)}
                          aria-label="Expand QR code"
                        >
                          <QrCode className="w-4 h-4" />
                          Expand QR
                        </Button>
                      }
                    />
                    
                    {/* Context-aware description */}
                    <p className={cn("text-sm text-center", semanticTokens.fg.muted)}>
                      {account.deposit_instructions?.iban && account.source_currency?.toUpperCase() === 'EUR' ? (
                        <>EPC-compatible QR code. Most EU mobile banking apps can scan and prefill payment details automatically.</>
                      ) : account.deposit_instructions?.iban ? (
                        <>Scan to quickly access SEPA bank details</>
                      ) : account.deposit_instructions?.routing ? (
                        <>Scan to view ACH/Wire bank details</>
                      ) : account.deposit_instructions?.clabe ? (
                        <>Scan to view SPEI details</>
                      ) : (
                        <>Scan to view bank transfer details</>
                      )}
                    </p>
                  </>
                ) : (
                  <div className={cn("text-center py-8", semanticTokens.fg.muted)}>
                    <p className={cn(semanticTokens.typography.body)}>QR code will appear here</p>
                    <p className={cn(semanticTokens.typography.caption, "mt-1")}>Once deposit instructions are available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </PageContent>
    </Page>
  );
};

// Simple timeline component for VA transactions
const TransactionTimeline: React.FC<{ accountId: string }> = ({ accountId }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Transaction History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn("text-center py-8", semanticTokens.fg.muted)}>
          <div className="mb-2">No transactions yet</div>
          <div className="text-sm">
            Transactions will appear here when deposits are made to this virtual account
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
